import { Link } from 'react-router-dom';
import { ProductCardProps } from '../types';
import { Image } from '../common/Image';

export const ProductCard: React.FC<ProductCardProps> = ({ product }) => {
  const isOctopus = product.id === 'octopus';
  const isRaven = product.id === 'raven';
  const isChordpond = product.id === 'chordpond';
  const isTeaser = !product.appStoreUrl && product.id !== 'raven' && product.id !== 'chordpond';
  
  // Product color palettes
  let accentColor = '#E5C65A'; // Gorilla Gold (default)
  let borderColor = 'rgba(229, 198, 90, 0.1)';
  let borderHoverColor = 'rgba(229, 198, 90, 0.3)';
  let ringColor = '#E5C65A';

  if (isOctopus) {
    accentColor = '#A37BFF';
    borderColor = 'rgba(163, 123, 255, 0.1)';
    borderHoverColor = 'rgba(163, 123, 255, 0.3)';
    ringColor = '#A37BFF';
  } else if (isRaven) {
    accentColor = '#4FA3D1';
    borderColor = 'rgba(79, 163, 209, 0.1)';
    borderHoverColor = 'rgba(79, 163, 209, 0.3)';
    ringColor = '#4FA3D1';
  } else if (isChordpond) {
    accentColor = '#5BBFA8';
    borderColor = 'rgba(91, 191, 168, 0.1)';
    borderHoverColor = 'rgba(91, 191, 168, 0.3)';
    ringColor = '#5BBFA8';
  }

  const CardContent = (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
      {/* Logo Section */}
      <div className="lg:col-span-5 flex items-center justify-center lg:justify-center h-[280px] lg:h-[320px]">
        <div className="block w-full max-w-[240px] lg:max-w-[260px] mx-auto">
          {product.logo.url ? (
            <Image
              src={product.logo.url}
              alt={product.logo.alt}
              className={`w-full h-auto opacity-90${isChordpond ? ' rounded-[22%]' : ''}`}
              style={{ maxWidth: '85%', color: accentColor }}
            />
          ) : (
            <div className="w-full aspect-square flex items-center justify-center text-8xl">
              🐙
            </div>
          )}
        </div>
      </div>

      {/* Content Section */}
      <div className="lg:col-span-7 text-center lg:text-left self-start">
        {/* Product Name - Always at top */}
        <h3 className="text-3xl md:text-4xl font-black tracking-product font-mono text-primary mb-4">
          {product.name}
        </h3>

        {/* Tagline */}
        {product.tagline && (
          <>
            <p className="text-xl font-semibold mb-6 leading-tight" style={{ color: accentColor }}>
              {product.tagline}
            </p>
            
            {/* Description */}
            <p className="text-secondary text-lg mb-8 leading-relaxed">
              {product.description}
            </p>

          </>
        )}

        {/* Features */}
        {product.features && product.features.length > 0 && (
          <ul className="space-y-3 mb-10 text-left inline-block lg:block">
            {product.features.slice(0, 4).map((feature, index) => (
              <li key={index} className="flex items-start text-sm text-secondary">
                <span className="mr-3 mt-1" style={{ color: accentColor }}>•</span>
                {feature}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );

  if (isTeaser) {
    return (
      <div
        className="flex flex-col h-full bg-deepBlack rounded-[18px] p-6 md:p-8 shadow-[0_0_16px_rgba(0,0,0,0.25)] border transition-all relative"
        style={{ 
          textDecoration: 'none',
          borderColor: borderColor,
          cursor: 'default'
        }}
      >
        {CardContent}
      </div>
    );
  }

  return (
    <Link
      to={`/products/${product.id}`}
      className="flex flex-col h-full bg-deepBlack rounded-[18px] p-6 md:p-8 shadow-[0_0_16px_rgba(0,0,0,0.25)] border transition-all relative group focus:outline-none focus:ring-2"
      style={{ 
        textDecoration: 'none',
        borderColor: borderColor,
        '--hover-border-color': borderHoverColor,
        '--ring-color': ringColor
      } as React.CSSProperties & { '--hover-border-color': string; '--ring-color': string }}
      onMouseEnter={(e) => e.currentTarget.style.borderColor = borderHoverColor}
      onMouseLeave={(e) => e.currentTarget.style.borderColor = borderColor}
    >
      {CardContent}
    </Link>
  );
};
